// ./src/components/features/Embeddings/EmbeddingsTable/EmbeddingsTable.tsx
import React, { useMemo } from "react";
import { Text, Badge } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
} from "@/components/generic";

import { Embedding } from "@/utils/api/EmbeddingService";

export interface EmbeddingsTableProps {
  /** Array of embeddings to display */
  embeddings: Embedding[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditEmbedding?: (embedding: Embedding) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateEmbedding?: (embedding: Embedding) => void;

  /** Callback when remove action is clicked */
  onRemoveEmbedding?: (embedding: Embedding) => void;

  /** Callback when view action is clicked */
  onViewEmbedding?: (embedding: Embedding) => void;

  /** ID of embedding currently being removed */
  removingEmbeddingId?: string | null;

  /** ID of embedding just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const EmbeddingsTable: React.FC<EmbeddingsTableProps> = ({
  embeddings,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditEmbedding,
  onDuplicateEmbedding,
  onRemoveEmbedding,
  onViewEmbedding,
  removingEmbeddingId,
  editedItemId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Embedding>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Untitled Embedding"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
            {record.venue && (
              <Text size="xs" c="dimmed" mt={4}>
                Venue: {record.venue.name}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "layoutType",
        label: "Layout",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;
          return (
            <Badge variant="light" size="sm">
              {value}
            </Badge>
          );
        },
      },
      {
        key: "scheduleType",
        label: "Schedule Type",
        sortable: true,
        width: 200,
        minWidth: 200,
        align: "left",
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;
          return (
            <Badge variant="light" size="sm">
              {value}
            </Badge>
          );
        },
      },
      {
        key: "totalRequests",
        label: "Requests",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return (
            <Text size="sm" fw={500}>
              {value != null ? value.toLocaleString() : "0"}
            </Text>
          );
        },
      },
      {
        key: "actions",
        label: "",
        width: 230,
        minWidth: 230,
        align: "left",
        render: (_, record: any) => (
          <RecordActions
            context={record}
            onViewClick={onViewEmbedding}
            onEditClick={onEditEmbedding}
            onRemoveClick={onRemoveEmbedding}
            onDuplicateClick={onDuplicateEmbedding}
            removeInProgress={removingEmbeddingId === record.id}
            viewTooltip="Preview the embedding"
          />
        ),
      },
    ],
    [
      onEditEmbedding,
      onDuplicateEmbedding,
      onRemoveEmbedding,
      onViewEmbedding,
      removingEmbeddingId,
    ]
  );

  return (
    <AppTable
      data={embeddings}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(embedding) => embedding?.id || ""}
      highlightRow={(embedding) => embedding?.id == editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
